import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
  Grid,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MappingDiagram from './MappingDiagram';
import MappingControls from './MappingControls';
import useMappingAnswer from './useMappingAnswer';
import { defaultMappingConfig, createItem, stringifyAnswer } from './mappingUtils';
import 'mathlive';

/**
 * Admin component for configuring mapping questions
 * Allows adding/removing items, setting rules, and defining correct answer
 */
const MappingAdmin = ({
  config = defaultMappingConfig,
  answer = '',
  onConfigChange,
  onAnswerChange,
}) => {
  const [activeTab, setActiveTab] = useState('items');

  // Hook for managing answer connections
  const {
    connections,
    selectedLeft,
    canUndo,
    canClear,
    handleLeftClick,
    handleRightClick,
    handleConnectionClick,
    undo,
    clear,
    syncWithExternalValue,
  } = useMappingAnswer(answer, config, onAnswerChange);

  // Sync with external answer changes (e.g., when loading existing question)
  useEffect(() => {
    syncWithExternalValue(answer);
  }, [answer, syncWithExternalValue]);

  // Update config helper
  const updateConfig = useCallback(
    (updates) => {
      if (onConfigChange) {
        onConfigChange({ ...config, ...updates });
      }
    },
    [config, onConfigChange]
  );

  // Add new item
  const addItem = (side) => {
    const prefix = side === 'left' ? 'L' : 'R';
    const itemsKey = side === 'left' ? 'leftItems' : 'rightItems';
    const newItem = createItem(prefix);
    updateConfig({
      [itemsKey]: [...config[itemsKey], newItem],
    });
  };

  // Remove item
  const removeItem = (side, itemId) => {
    const itemsKey = side === 'left' ? 'leftItems' : 'rightItems';
    const updatedItems = config[itemsKey].filter((item) => item.id !== itemId);

    // Also remove any connections involving this item
    const updatedConnections = connections.filter((conn) =>
      side === 'left' ? conn.from !== itemId : conn.to !== itemId
    );

    updateConfig({ [itemsKey]: updatedItems });

    if (updatedConnections.length !== connections.length && onAnswerChange) {
      onAnswerChange(stringifyAnswer(updatedConnections));
    }
  };

  // Update item value
  const updateItemValue = (side, itemId, value) => {
    const itemsKey = side === 'left' ? 'leftItems' : 'rightItems';
    const updatedItems = config[itemsKey].map((item) =>
      item.id === itemId ? { ...item, value } : item
    );
    updateConfig({ [itemsKey]: updatedItems });
  };

  // Update item type
  const updateItemType = (side, itemId, type) => {
    const itemsKey = side === 'left' ? 'leftItems' : 'rightItems';
    const updatedItems = config[itemsKey].map((item) =>
      item.id === itemId ? { ...item, type } : item
    );
    updateConfig({ [itemsKey]: updatedItems });
  };

  // Render item editor
  const renderItemEditor = (item, index, side) => (
    <Box
      key={item.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1,
      }}
    >
      <Typography variant="body2" sx={{ minWidth: 24 }}>
        {index + 1}.
      </Typography>
      {item.type === 'math' ? (
        <Box
          sx={{
            flex: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            minHeight: 40,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <math-field
            value={item.value || ''}
            onInput={(evt) => updateItemValue(side, item.id, evt.target.value)}
            style={{
              width: '100%',
              border: 'none',
              fontSize: '14px',
            }}
          />
        </Box>
      ) : (
        <TextField
          size="small"
          value={item.value}
          onChange={(e) => updateItemValue(side, item.id, e.target.value)}
          placeholder={`Item ${index + 1}`}
          sx={{ flex: 1 }}
        />
      )}
      <ToggleButtonGroup
        size="small"
        value={item.type || 'text'}
        exclusive
        onChange={(e, newType) => {
          if (newType) updateItemType(side, item.id, newType);
        }}
      >
        <ToggleButton value="text">Text</ToggleButton>
        <ToggleButton value="math">Math</ToggleButton>
      </ToggleButtonGroup>
      <Tooltip title="Remove item">
        <span>
          <IconButton
            size="small"
            color="error"
            onClick={() => removeItem(side, item.id)}
            disabled={config[side === 'left' ? 'leftItems' : 'rightItems'].length <= 1}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Tab selector */}
      <ToggleButtonGroup
        value={activeTab}
        exclusive
        onChange={(e, newTab) => {
          if (newTab) setActiveTab(newTab);
        }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="items">Items & Settings</ToggleButton>
        <ToggleButton value="answer">Set Correct Answer</ToggleButton>
      </ToggleButtonGroup>

      {activeTab === 'items' && (
        <Grid container spacing={3}>
          {/* Left column items */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Left Column ({config.leftLabel})
                </Typography>
                <TextField
                  size="small"
                  label="Label"
                  value={config.leftLabel}
                  onChange={(e) => updateConfig({ leftLabel: e.target.value })}
                  sx={{ width: 80 }}
                />
              </Box>

              {config.leftItems.map((item, i) => renderItemEditor(item, i, 'left'))}

              <Button
                startIcon={<AddIcon />}
                onClick={() => addItem('left')}
                size="small"
                sx={{ mt: 1 }}
              >
                Add Item
              </Button>
            </Paper>
          </Grid>

          {/* Right column items */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Right Column ({config.rightLabel})
                </Typography>
                <TextField
                  size="small"
                  label="Label"
                  value={config.rightLabel}
                  onChange={(e) => updateConfig({ rightLabel: e.target.value })}
                  sx={{ width: 80 }}
                />
              </Box>

              {config.rightItems.map((item, i) => renderItemEditor(item, i, 'right'))}

              <Button
                startIcon={<AddIcon />}
                onClick={() => addItem('right')}
                size="small"
                sx={{ mt: 1 }}
              >
                Add Item
              </Button>
            </Paper>
          </Grid>

          {/* Settings */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Mapping Rules
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={config.allowMultipleFromLeft}
                        onChange={(e) =>
                          updateConfig({ allowMultipleFromLeft: e.target.checked })
                        }
                      />
                    }
                    label="Allow multiple connections from one left item"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={config.allowMultipleToRight}
                        onChange={(e) =>
                          updateConfig({ allowMultipleToRight: e.target.checked })
                        }
                      />
                    }
                    label="Allow multiple connections to one right item"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={config.requireAllLeftMapped}
                        onChange={(e) =>
                          updateConfig({ requireAllLeftMapped: e.target.checked })
                        }
                      />
                    }
                    label="Require all left items to be connected"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Display Options
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={config.showLabels}
                        onChange={(e) => updateConfig({ showLabels: e.target.checked })}
                      />
                    }
                    label="Show column labels"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={config.ovalShape}
                        onChange={(e) => updateConfig({ ovalShape: e.target.checked })}
                      />
                    }
                    label="Use oval shape (vs rectangle)"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Grading
              </Typography>

              <ToggleButtonGroup
                value={config.grading?.mode || 'exact'}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    updateConfig({
                      grading: { ...config.grading, mode: newMode },
                    });
                  }
                }}
                size="small"
              >
                <ToggleButton value="exact">
                  Exact Match (all or nothing)
                </ToggleButton>
                <ToggleButton value="partial">
                  Partial Credit
                </ToggleButton>
              </ToggleButtonGroup>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 'answer' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click on items to create the correct answer connections. Click on an arrow
            to remove it.
          </Typography>

          <MappingControls
            onUndo={undo}
            onClear={clear}
            canUndo={canUndo}
            canClear={canClear}
          />

          <Paper sx={{ p: 2 }}>
            <MappingDiagram
              leftItems={config.leftItems}
              rightItems={config.rightItems}
              connections={connections}
              selectedLeft={selectedLeft}
              onLeftClick={handleLeftClick}
              onRightClick={handleRightClick}
              onConnectionClick={handleConnectionClick}
              config={config}
            />
          </Paper>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Current connections: {connections.length}
            </Typography>
            {connections.length > 0 && (
              <Box sx={{ mt: 1, fontSize: '0.85rem' }}>
                {connections.map((conn, i) => {
                  const leftItem = config.leftItems.find((item) => item.id === conn.from);
                  const rightItem = config.rightItems.find((item) => item.id === conn.to);
                  return (
                    <div key={i}>
                      {leftItem?.value || 'Item'} → {rightItem?.value || 'Item'}
                    </div>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MappingAdmin;
